import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  OnInit,
  ViewChild,
  ElementRef,
  AfterViewInit,
  forwardRef,
  SimpleChanges,
  OnDestroy,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import Prism from 'prismjs';
import 'prismjs/components/prism-python';
import { parser } from '@lezer/python';
import { SyntaxNode } from '@lezer/common';
import { Subject, firstValueFrom, from } from 'rxjs';
import { Store } from '@ngrx/store';
import {
  takeUntil,
  debounceTime,
  distinctUntilChanged,
  switchMap,
} from 'rxjs/operators';
import * as piDataActions from '../../../store/actions/pi-data.action';
import {
  selectSelectedPiPoints,
  selectUnusedSelectedPiPointsGlobal,
} from '../../../store/selectors/pi-data.selector';
import { PiDataService } from '../../../services/pi-data.service';
import { PiPoint } from '../../../models/pi-data.model';

@Component({
  selector: 'app-live-code-display',
  templateUrl: './live-code-display.component.html',
  styleUrl: './live-code-display.component.scss',
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LiveCodeDisplayComponent),
      multi: true,
    },
  ],
})
export class LiveCodeDisplayComponent
  implements OnChanges, AfterViewInit, ControlValueAccessor, OnDestroy
{
  @ViewChild('codeTextarea', { static: false })
  codeTextarea!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('highlightedCodePre', { static: false })
  highlightedCodePre!: ElementRef<HTMLPreElement>;

  @Input() ruleFormula?: string = '';
  @Input() pythonCode?: string = '';
  @Input() editable: boolean = false;
  @Input() placeholder: string = 'Entrez votre code Python ici...';
  @Input() validateSyntax: boolean = true;
  @Input() minHeight: string = '150px';
  @Input() showExampleCode: boolean = true;
  @Input() enablePiPointValidation: boolean = true;
  @Input() isLoadingExistingPiPoints: boolean = false;
  @Input() componentId!: string;

  @Output() codeChange = new EventEmitter<string>();
  @Output() syntaxValidation = new EventEmitter<{
    isValid: boolean;
    error?: string;
  }>();
  @Output() piPointValidation = new EventEmitter<{
    isValid: boolean;
    errors: string[];
  }>();
  @Output() piPointValidationLoading = new EventEmitter<boolean>();

  highlightedCode: string = '';
  currentCode: string = '';
  syntaxError: string | null = null;
  isValidSyntax: boolean = true;

  piPointSearchTerm: string = '';
  piPointSearchResults: PiPoint[] = [];
  piPointSearchPerformed: boolean = false;
  selectedPiPoints: PiPoint[] = [];
  private recentlyRemovedPiPoints: Set<PiPoint> = new Set<PiPoint>();
  private previousSelectedPiPoints: Set<PiPoint> = new Set<PiPoint>();
  piPointValidationErrors: string[] = [];
  isPiPointValid: boolean = false;
  isSearchingPiPoint: boolean = false;
  unusedSelectedPiPointsGlobal: string[] = [];

  // Example code that won't be saved
  private readonly exampleCode: string = `def eval_rule(df):
    pass`;
  isShowingExample: boolean = false;

  // ControlValueAccessor properties
  private onChange = (value: string) => {};
  public onTouched = () => {};
  disabled = false;

  private destroy$ = new Subject<void>();
  // Trigger subject for PI point validation; we debounce this with RxJS
  private piValidationTrigger: Subject<string> = new Subject<string>();

  constructor(
    private store: Store,
    private piDataService: PiDataService,
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.piValidationTrigger.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['pythonCode'] &&
      changes['pythonCode'].currentValue !== undefined
    ) {
      this.currentCode = this.pythonCode || '';
      this.updateHighlightedCode();
      this.validatePythonSyntax();
      this.isShowingExample = false; // Reset example state when code is set externally
      if (this.componentId) {
        this.store.dispatch(
          piDataActions.setLiveCode({
            componentId: this.componentId,
            code: this.currentCode,
          }),
        );
      }
    }
  }

  ngAfterViewInit() {
    this.store
      .select(selectUnusedSelectedPiPointsGlobal)
      .pipe(takeUntil(this.destroy$))
      .subscribe((unusedGlobal) => {
        this.unusedSelectedPiPointsGlobal = unusedGlobal;
        this.piPointValidation.emit({
          isValid: this.unusedSelectedPiPointsGlobal.length === 0,
          errors: [],
        });
      });

    this.store
      .select(selectSelectedPiPoints)
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedPiPointsArr) => {
        const newSet = new Set<PiPoint>(selectedPiPointsArr.map((s) => s));
        const removed = [...this.previousSelectedPiPoints].filter(
          (p) => !newSet.has(p),
        );
        if (removed.length > 0) {
          for (const removedPi of removed) {
            this.recentlyRemovedPiPoints.add(removedPi);
            if (this.currentCode) {
              this.currentCode = this.replacePiPointInCode(
                this.currentCode,
                removedPi,
              );
            }
          }
          this.updateHighlightedCode();
          this.validatePythonSyntax();
          const codeToEmit = this.isShowingExample ? '' : this.currentCode;
          this.codeChange.emit(codeToEmit);
          this.onChange(codeToEmit);
        }
        this.selectedPiPoints = selectedPiPointsArr;
        this.previousSelectedPiPoints = newSet;
        this.validateDataFramePiPoints();
      });

    this.piValidationTrigger
      .pipe(
        distinctUntilChanged(),
        debounceTime(300),
        switchMap((code) => from(this.runPiValidation(code))),
        takeUntil(this.destroy$),
      )
      .subscribe();

    setTimeout(() => {
      this.initializeCode();
      this.updateHighlightedCode();
      if (this.codeTextarea) {
        this.syncScroll();
      }
      if (this.componentId) {
        this.store.dispatch(
          piDataActions.setLiveCode({
            componentId: this.componentId,
            code: this.currentCode,
          }),
        );
      }
    }, 0);
  }

  private initializeCode(): void {
    // Show example code if no code is provided and example is enabled
    if (!this.currentCode && this.showExampleCode && this.editable) {
      this.currentCode = this.exampleCode;
      this.isShowingExample = true;
      this.updateHighlightedCode();
      this.validatePythonSyntax();
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    this.currentCode = value || '';
    this.isShowingExample = false; // Reset example state when value is written
    this.updateHighlightedCode();
    this.validatePythonSyntax();
    if (this.componentId) {
      this.store.dispatch(
        piDataActions.setLiveCode({
          componentId: this.componentId,
          code: this.currentCode,
        }),
      );
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private updateHighlightedCode() {
    if (this.currentCode) {
      try {
        this.highlightedCode = Prism.highlight(
          this.currentCode,
          Prism.languages['python'],
          'python',
        );
      } catch (error) {
        console.warn('Error highlighting code:', error);
        this.highlightedCode = this.escapeHtml(this.currentCode);
      }
    } else {
      this.highlightedCode = '';
    }
  }

  private validatePythonSyntax() {
    if (!this.validateSyntax || !this.currentCode.trim()) {
      this.isValidSyntax = true;
      this.syntaxError = null;
      this.syntaxValidation.emit({ isValid: true });
      return;
    }

    try {
      // Use Lezer Python parser
      const tree = parser.parse(this.currentCode);
      const syntaxErrors = this.getDetailedSyntaxErrors(tree.topNode);

      this.isValidSyntax = syntaxErrors.length === 0;
      this.syntaxError =
        syntaxErrors.length > 0
          ? `Erreur de syntaxe Python détectée: ${syntaxErrors[0].message}`
          : null;

      this.syntaxValidation.emit({
        isValid: this.isValidSyntax,
        error: this.syntaxError || undefined,
      });

      this.validateDataFramePiPoints();
    } catch (error) {
      console.error('Parser error:', error);
      this.isValidSyntax = false;
      this.syntaxError = `Erreur de syntaxe Python détectée: ${error}`;
      this.syntaxValidation.emit({
        isValid: false,
        error: this.syntaxError,
      });
    }
  }

  onPiPointSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.piPointSearchTerm = target.value.toUpperCase();
    this.piPointSearchResults = [];
    this.piPointSearchPerformed = false;
    this.isPiPointValid = false;
    this.isSearchingPiPoint = false;
  }

  async searchPiPoint(): Promise<void> {
    const searchTerm = this.piPointSearchTerm.trim();

    if (!searchTerm) {
      this.piPointSearchResults = [];
      this.piPointSearchPerformed = false;
      this.isPiPointValid = false;
      this.isSearchingPiPoint = false;
      return;
    }

    this.piPointSearchPerformed = true;
    this.isSearchingPiPoint = true;

    try {
      const piPoint = await firstValueFrom(
        this.piDataService.GetPiPointForTag(searchTerm.toUpperCase()),
      );
      this.isPiPointValid =
        piPoint &&
        piPoint.tag?.length > 0 &&
        piPoint.webId?.length > 0 &&
        piPoint.server?.length > 0
          ? true
          : false;
      this.piPointSearchResults = piPoint ? [piPoint] : [];
    } catch (error) {
      this.isPiPointValid = false;
      this.piPointSearchResults = [];
    } finally {
      this.isSearchingPiPoint = false;
    }
  }

  selectPiPoint(piPoint: PiPoint): void {
    this.store.dispatch(piDataActions.addSelectedPiPoint({ piPoint: piPoint }));

    this.piPointSearchTerm = '';
    this.piPointSearchResults = [];
    this.piPointSearchPerformed = false;
    this.isPiPointValid = false;
    this.isSearchingPiPoint = false;
  }

  removeSelectedPiPoint(piPoint: PiPoint): void {
    this.recentlyRemovedPiPoints.add(piPoint);

    if (this.currentCode) {
      this.currentCode = this.replacePiPointInCode(this.currentCode, piPoint);
      this.updateHighlightedCode();
      this.validatePythonSyntax();
      const codeToEmit = this.isShowingExample ? '' : this.currentCode;
      this.codeChange.emit(codeToEmit);
      this.onChange(codeToEmit);
    }
    this.store.dispatch(piDataActions.removeSelectedPiPoint({ piPoint }));
  }

  clearAllSelectedPiPoints(): void {
    this.store.dispatch(piDataActions.clearSelectedPiPoints());
  }

  private validateDataFramePiPoints(): void {
    this.piValidationTrigger.next(this.currentCode || '');
  }

  private async runPiValidation(code: string): Promise<void> {
    this.piPointValidationErrors = [];
    if (!code) {
      this.piPointValidation.emit({ isValid: true, errors: [] });
      this.piPointValidationLoading.emit(false);
      return;
    }

    this.piPointValidationLoading.emit(true);

    const emptyPiPatterns = [
      /df\s*\[\s*''\s*\]/g,
      /df\s*\[\s*""\s*\]/g,
      /df\s*\[\s*`\s*`\s*\]/g,
      /df\.\w+\s*\[[^,]+,\s*''\s*\]/g,
      /df\.\w+\s*\[[^,]+,\s*""\s*\]/g,
      /df\.\w+\s*\[[^,]+,\s*`\s*`\s*\]/g,
      /df\.[^[]+\[''\]/g,
      /df\.[^[]+\[""\]/g,
      /df\.[^[]+\[``\]/g,
    ];
    let hasEmptyPiPoint = false;
    for (const pattern of emptyPiPatterns) {
      if (pattern.test(code)) {
        hasEmptyPiPoint = true;
        break;
      }
    }

    if (hasEmptyPiPoint) {
      this.piPointValidationErrors.push(
        `Vous avez un df sans point PI. Veuillez renseigner un point PI.`,
      );
      this.piPointValidation.emit({
        isValid: this.piPointValidationErrors.length === 0,
        errors: this.piPointValidationErrors,
      });
      return;
    }

    const foundTagsSet = this.extractPiPointsFromCode(code);
    const foundTags = Array.from(foundTagsSet);
    foundTags.sort((a, b) => b.length - a.length);
    const uniqueTags: string[] = [];
    for (const t of foundTags) {
      const up = t.toUpperCase();
      const isSubstring = uniqueTags.some((kept) => kept.toUpperCase() === up);
      if (!isSubstring) uniqueTags.push(t);
    }

    for (const piPointInCode of uniqueTags) {
      const upperPi = piPointInCode.toUpperCase();
      const alreadySelected = this.selectedPiPoints.some(
        (s) => s.tag?.toUpperCase() === upperPi,
      );
      if (alreadySelected) {
        continue;
      }

      if (upperPi.length < 8) {
        this.piPointValidationErrors.push(
          `Le point Pi "${piPointInCode}" est trop court pour être valide.`,
        );
        continue;
      }
      try {
        const piPoint = await firstValueFrom(
          this.piDataService.GetPiPointForTag(upperPi),
        );

        if (
          piPoint &&
          piPoint.tag?.length &&
          piPoint.webId?.length &&
          piPoint.server?.length
        ) {
          const hasOverlappingSelected = this.selectedPiPoints.some((s) => {
            const upS = s.tag?.toUpperCase();
            return (
              upS !== upperPi &&
              (upS.includes(upperPi) || upperPi.includes(upS))
            );
          });

          if (!hasOverlappingSelected) {
            this.store.dispatch(
              piDataActions.addSelectedPiPoint({ piPoint: piPoint }),
            );
          } else {
            continue;
          }
        } else {
          this.piPointValidationErrors.push(
            `Le point Pi "${piPointInCode}" n'existe pas dans le système.`,
          );
        }
      } catch (error) {
        this.piPointValidationErrors.push(
          `Erreur lors de la vérification du point Pi "${piPointInCode}".`,
        );
      }
    }

    this.recentlyRemovedPiPoints.clear();

    this.piPointValidation.emit({
      isValid: this.piPointValidationErrors.length === 0,
      errors: this.piPointValidationErrors,
    });
    this.piPointValidationLoading.emit(false);
  }

  private extractPiPointsFromCode(code: string): Set<string> {
    const patterns = [
      /df\s*\[\s*'([^']+)'\s*\]/g,
      /df\s*\[\s*"([^"]+)"\s*\]/g,
      /df\s*\[\s*`([^`]+)`\s*\]/g,
      /df\.\w+\s*\[[^,]+,\s*'([^']+)'\s*\]/g,
      /df\.\w+\s*\[[^,]+,\s*"([^"]+)"\s*\]/g,
      /df\.\w+\s*\[[^,]+,\s*`([^`]+)`\s*\]/g,
      /df\.[^[]+\['([^']+)'\]/g,
      /df\.[^[]+\["([^"]+)"\]/g,
      /df\.[^[]+\[`([^`]+)`\]/g,
    ];
    const foundTags = new Set<string>();
    for (const pattern of patterns) {
      let match;
      while ((match = pattern.exec(code)) !== null) {
        if (match[1]) {
          foundTags.add(match[1].trim());
        }
      }
    }
    return foundTags;
  }

  private replacePiPointInCode(code: string, piPoint: PiPoint): string {
    const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const p = esc(piPoint.tag);
    const pattern = new RegExp(
      String.raw`(\bdf(?:\.\w+)?\s*\[(?:[^,\]]+,)?\s*)(['"\`])${p}\2(\s*\])`,
      'g',
    );

    return code.replace(
      pattern,
      (_m, pre: string, q: string, post: string) => `${pre}${q}${q}${post}`,
    );
  }

  private getDetailedSyntaxErrors(node: SyntaxNode): Array<{
    message: string;
    line: number;
    column: number;
    position: number;
    length: number;
    context: string;
  }> {
    const errors: Array<{
      message: string;
      line: number;
      column: number;
      position: number;
      length: number;
      context: string;
    }> = [];

    const findErrors = (currentNode: SyntaxNode) => {
      // Check if this node is an error node
      if (currentNode.type.isError || currentNode.name === '⚠') {
        const position = currentNode.from;
        const length = currentNode.to - currentNode.from;
        const lineInfo = this.getLineAndColumn(position);
        const context = this.getErrorContext(position, length);

        let message = 'Erreur de syntaxe';

        // Try to provide more specific error messages based on context
        if (currentNode.parent) {
          message = this.getSpecificErrorMessage(currentNode, context);
        }

        errors.push({
          message,
          line: lineInfo.line,
          column: lineInfo.column,
          position,
          length,
          context,
        });
      }

      // Recursively check children
      let cursor = currentNode.cursor();
      if (cursor.firstChild()) {
        do {
          findErrors(cursor.node);
        } while (cursor.nextSibling());
      }
    };

    findErrors(node);
    return errors;
  }

  private getLineAndColumn(position: number): { line: number; column: number } {
    const lines = this.currentCode.substring(0, position).split('\n');
    return {
      line: lines.length,
      column: lines[lines.length - 1].length + 1,
    };
  }

  private getErrorContext(position: number, length: number): string {
    const start = Math.max(0, position - 20);
    const end = Math.min(this.currentCode.length, position + length + 20);
    const context = this.currentCode.substring(start, end);

    // Mark the error position with indicators
    const beforeError = this.currentCode.substring(start, position);
    const errorText = this.currentCode.substring(position, position + length);
    const afterError = this.currentCode.substring(position + length, end);

    return `${beforeError}>>>${errorText}<<<${afterError}`;
  }

  private getSpecificErrorMessage(
    errorNode: SyntaxNode,
    context: string,
  ): string {
    const parentType = errorNode.parent?.name;
    const precedingText = context.split('>>>')[0].trim();
    const errorText = context.split('>>>')[1]?.split('<<<')[0] || '';

    // Provide specific error messages based on common Python syntax errors
    if (parentType === 'ExpressionStatement' && errorText === '') {
      return 'Expression incomplète';
    }

    if (precedingText.endsWith('def') && errorText === '') {
      return 'Nom de fonction manquant après "def"';
    }

    if (precedingText.endsWith('if') && errorText === '') {
      return 'Condition manquante après "if"';
    }

    if (precedingText.endsWith('for') && errorText === '') {
      return 'Variable ou expression manquante après "for"';
    }

    if (precedingText.endsWith('import') && errorText === '') {
      return 'Nom de module manquant après "import"';
    }

    if (errorText.includes('(') && !errorText.includes(')')) {
      return 'Parenthèse fermante manquante';
    }

    if (errorText.includes('[') && !errorText.includes(']')) {
      return 'Crochet fermant manquant';
    }

    if (errorText.includes('{') && !errorText.includes('}')) {
      return 'Accolade fermante manquante';
    }

    if (errorText.includes('"') && errorText.split('"').length % 2 === 0) {
      return 'Guillemet fermant manquant';
    }

    if (errorText.includes("'") && errorText.split("'").length % 2 === 0) {
      return 'Apostrophe fermante manquante';
    }

    if (precedingText.endsWith(':') && errorText.trim() === '') {
      return 'Bloc de code attendu après ":"';
    }

    // Check for indentation errors
    if (parentType === 'Body' || parentType === 'Suite') {
      return "Erreur d'indentation";
    }

    return `Élément inattendu "${errorText}"`;
  }

  private checkForSyntaxErrors(node: any): boolean {
    if (!node) return false;

    // Check if this node is an ERROR node
    if (node.type === 'ERROR') {
      return true;
    }

    // Recursively check children
    if (node.children) {
      for (const child of node.children) {
        if (this.checkForSyntaxErrors(child)) {
          return true;
        }
      }
    }

    return false;
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  onCodeInput(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.currentCode = target.value;

    if (this.isShowingExample) {
      this.isShowingExample = false;
    }
    this.updateHighlightedCode();
    this.syncScroll();
    this.validatePythonSyntax();

    if (this.componentId) {
      this.store.dispatch(
        piDataActions.setLiveCode({
          componentId: this.componentId,
          code: this.currentCode,
        }),
      );
    }

    const codeToEmit = this.isShowingExample ? '' : this.currentCode;
    this.codeChange.emit(codeToEmit);
    this.onChange(codeToEmit);
  }

  onPythonSyntaxValidation(): void {
    // Public method to trigger validation manually
    this.validatePythonSyntax();
  }

  onKeyDown(event: KeyboardEvent): void {
    // Handle Tab key for indentation
    if (event.key === 'Tab') {
      event.preventDefault();
      const textarea = event.target as HTMLTextAreaElement;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;

      // Insert 4 spaces for indentation
      const spaces = '    ';
      textarea.value =
        textarea.value.substring(0, start) +
        spaces +
        textarea.value.substring(end);
      textarea.selectionStart = textarea.selectionEnd = start + spaces.length;

      // Trigger input event
      this.onCodeInput(event);
    }
  }

  onTextareaPaste(event: ClipboardEvent): void {
    requestAnimationFrame(() => {
      if (this.codeTextarea && this.codeTextarea.nativeElement) {
        const ta = this.codeTextarea.nativeElement;
        const syntheticEvent = { target: ta } as unknown as Event;
        this.onCodeInput(syntheticEvent);
      }
    });
  }

  onTextareaDrop(event: DragEvent): void {
    requestAnimationFrame(() => {
      if (this.codeTextarea && this.codeTextarea.nativeElement) {
        const ta = this.codeTextarea.nativeElement;
        const syntheticEvent = { target: ta } as unknown as Event;
        this.onCodeInput(syntheticEvent);
      }
    });
  }

  private syncScroll(): void {
    if (this.codeTextarea && this.highlightedCodePre) {
      const textarea = this.codeTextarea.nativeElement;
      const pre = this.highlightedCodePre.nativeElement;

      // Sync scroll positions
      pre.scrollTop = textarea.scrollTop;
      pre.scrollLeft = textarea.scrollLeft;
    }
  }

  onTextareaScroll(): void {
    // Use requestAnimationFrame for smoother scrolling
    requestAnimationFrame(() => {
      this.syncScroll();
    });
  }
}
